import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, Modal, PageHeader, Pagination, PillFilter, SearchBox } from '../components/ui'
import { DataTable } from '../components/DataTable'
import { useItem, useList } from '../api/queries'
import type { Measurement, Plant } from '../api/types'
import { loadComparison } from '../features/measurements/models'
import { MeasurementsTable } from '../features/tables/tables'
import { useDebounced } from '../lib/hooks'

type Flag = '' | 'off' | 'not_measured' | 'needs_remeasure'

export function MeasurementsPage() {
  const [params] = useSearchParams()
  const plantParam = params.get('plant') ?? ''
  const plantId = /^\d+$/.test(plantParam) ? Number(plantParam) : undefined
  const dateFrom = params.get('from') || undefined
  const dateTo = params.get('to') || undefined
  const [flag, setFlag] = useState<Flag>('')
  const [search, setSearch] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const [compare, setCompare] = useState<Measurement | null>(null)
  const q = useDebounced(search)
  const plant = useItem<Plant>(plantId ? `/api/plants/${plantId}/` : null)
  const list = useList<Measurement>('/api/measurements/', {
    plant: plantId, measured_after: dateFrom, measured_before: dateTo,
    operating_state: flag === 'off' ? 'off' : undefined, not_measured: flag === 'not_measured' ? 'true' : undefined,
    needs_remeasure: flag === 'needs_remeasure' ? 'true' : undefined, search: q, page: pageNo, ordering: '-measured_on',
  })
  const mismatching = list.data?.results.filter((m) => m.mismatches.length) ?? []
  const pick = (f: Flag) => { setFlag(f); setPageNo(1) }
  return (
    <div className="page">
      <PageHeader title={plantId ? `Mediciones de ${plant.data?.name ?? 'la planta'}` : 'Mediciones'} subtitle="Lecturas de carga eléctrica. Las filas apagadas y sin medir nunca se muestran como 0" />
      <Card>
        <div className="toolbar"><SearchBox label="Buscar mediciones" value={search} onChange={setSearch} /></div>
        <div className="pills" role="group" aria-label="Indicador">
          <PillFilter active={flag === ''} onClick={() => pick('')}>Todas</PillFilter>
          <PillFilter active={flag === 'off'} onClick={() => pick('off')}>Apagado</PillFilter>
          <PillFilter active={flag === 'not_measured'} onClick={() => pick('not_measured')}>Sin medir</PillFilter>
          <PillFilter active={flag === 'needs_remeasure'} onClick={() => pick('needs_remeasure')}>Requiere nueva medición</PillFilter>
        </div>
        <MeasurementsTable rows={list.data?.results} loading={list.isPending} error={list.error} onRetry={() => void list.refetch()} />
        {mismatching.length ? (
          <div className="stack">
            <h3>Reportado frente a calculado</h3>
            <p className="muted">Estas filas tienen valores reportados por la fuente que difieren de las fórmulas documentadas. Los datos de entrada de las hojas están redondeados, por lo que las diferencias pequeñas son normales.</p>
            <ul className="plain">{mismatching.map((m) => <li key={m.id}><button type="button" className="btn btn--ghost btn--sm" aria-label={`Comparar ${m.equipment_name}`} onClick={() => setCompare(m)}>Comparar {m.equipment_name}</button></li>)}</ul>
          </div>
        ) : null}
        {list.data ? <Pagination page={pageNo} pageSize={50} count={list.data.count} onPage={setPageNo} /> : null}
      </Card>
      {compare ? (
        <Modal title={`Reportado frente a calculado: ${compare.equipment_name}`} onClose={() => setCompare(null)}>
          {compare.warning ? <p className="callout callout--warn">{compare.warning}</p> : null}
          <DataTable caption="Reportado frente a calculado" rowKey={(r) => r.key} rows={loadComparison(compare)} columns={[
            { key: 'l', header: 'Magnitud', cell: (r) => r.label },
            { key: 'r', header: 'Reportado por la hoja', cell: (r) => <span className="mono">{r.reported}</span>, align: 'right' },
            { key: 'c', header: 'Calculado', cell: (r) => <span className="mono">{r.computed}</span>, align: 'right' },
            { key: 'm', header: 'Coincidencia', cell: (r) => (r.mismatch ? 'Difiere' : 'Coincide') },
          ]} />
        </Modal>
      ) : null}
    </div>
  )
}
